import { message } from 'antd'
import { useEffect, useReducer, useRef } from 'react'
import { history } from '../../redux/store'
import {
  AcceptInfoItem,
  DOWNLOAD_ACCEPT_TEMP,
  GET_INVOICE_INFO_BY_ID,
  GET_ORDER_DETAIL,
  InvoiceInfoParam,
  SUBMIT_ORDER_ACCEPT,
  GET_ORDER_PRODUCT_INFO,
  BATCH_GET_PRODUCT_PICTURE,
  DOWNLOAD_PURCHASEORDERCONTRACT,
  UPLOAD_ORDER_PURCHASE_CONTRACT,
  DETELE_FILE,
  GET_ORDER_DESCRIPTION,
  ADD_ORDER_DESCRIPTION,
  UPLOAD_ORDER_DESCRIPTION,
  DOWNLOAD_CUSTOM_ACCEPT_TEMP,
} from '../../api/orderManage'
import {
  download,
  GetLocalStorage,
  isResponseCodeSuccess,
  timeFormatStr,
} from '../../tools/util'
import { useQuery } from '../../tools/utilHooks'
import moment from 'moment'
import { CustomerConfirm } from '../../components/ModalUser'
import { SUPROUTE } from '../../route'
import { handleJumpAddInvoice } from '../order-invoice/hooks'
import { WaterMarkOption } from '../../components/previewModal'
import reducer, {
  initialState,
  setCheckedRadioKeyAction,
  setSelectedRowsAction,
  setLoadingAction,
  setDetailInfoAction,
  setProductListAction,
  setCurrentRadioDictAction,
  setInvoiceInfoAction,
  setProgressValueAction,
  setUploadCertificateModalOptionAction,
  setCurrentEditCertificateAction,
  setIsPreviewModalVisibleAction,
  setPreviewFileUrlAction,
  setWaterMarkOptionAction,
  setIsTableLoadingAction,
  setDetailListDataAction,
  setIsOpenModalAction,
  setRichTextAction,
  setOrderDescriptionFilesAction,
} from './reducer'
import { MAX_SUPPLIER_DELIVERY_COUNT } from '../../global/config'
import { RcFile } from 'antd/lib/upload'
import {
  openInvoiceStateDict,
  openInvoiceStateDictInvert,
} from '../../tools/state'

const ORDER_TRANSFER_INVOICE_BLOCK_MSG =
  '当前订单开票状态为“开票、开票中”若需要转派请联系咸亨相关人员进行处理！'
const ORDER_ACCEPT_UPLOAD_DELAY_MILLIS = 3 * 60 * 60 * 1000

/** 仅「未开票」允许发起转派；其余（审核中/暂存/已开票/驳回等）拦截 */
function isSupplierInvoiceStatusBlockingTransfer(
  accountOpenInvoiceStatus: string | undefined,
): boolean {
  const raw = accountOpenInvoiceStatus
  const code = String(raw ?? '').trim()
  const unInvoiced = openInvoiceStateDict['未开票'].numstr
  if (code === unInvoiced || raw === '未开票') {
    return false
  }
  const nameFromCode = openInvoiceStateDictInvert?.[code]?.name
  if (nameFromCode === '未开票') {
    return false
  }
  return true
}

/** 可转派数量：物料行数量 - 取消数 - 退货数 */
function getProductTransferRemainQty(row: {
  num?: number
  cancelCount?: number
  reCount?: number
}): number {
  const n = Number(row?.num ?? 0)
  const cancel = Number(row?.cancelCount ?? 0)
  const ret = Number(row?.reCount ?? 0)
  if ([n, cancel, ret].some((v) => Number.isNaN(v))) {
    return 0
  }
  return n - cancel - ret
}

function getFullyReturnedOrCancelledRows(rows: any[]) {
  return rows.filter((row) => getProductTransferRemainQty(row) <= 0)
}

const UserOrderDetailHook = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const richTextRef = useRef<any>(null)
  const {
    detailInfo,
    selectedRows,
    productList,
    uploadCertificateModalOption,
    richText,
    orderDescriptionFiles,
  } = state
  const query = useQuery()
  const id = query.get('id') || ''
  const baseUrl = GetLocalStorage('BASEURL')
  /**
   * 表格多选选择框选中事件
   * @param key
   */
  const handleCheckedChange = (key: any) => {
    dispatch(setCheckedRadioKeyAction(key))
  }

  const handleTableChangeCheck = (
    selectedRowKeys: any,
    selectedRows: any[],
  ) => {
    dispatch(setSelectedRowsAction(selectedRows))
  }

  const refreshData = () => {
    getDetailInfo()
  }

  // 获取详情数据
  const getDetailInfo = async () => {
    dispatch(setLoadingAction(true))
    getInvoiceInfo({
      orderId: id,
    })
    const res = await GET_ORDER_DETAIL({
      orderId: id,
    })
    dispatch(setLoadingAction(false))
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setDetailInfoAction(res.data))
    } else {
      message.warning(res.msg)
    }
    dispatch(setIsTableLoadingAction(true))
    const resList = await GET_ORDER_PRODUCT_INFO({
      orderId: id,
    })
    dispatch(setIsTableLoadingAction(false))
    if (isResponseCodeSuccess(resList.code)) {
      dispatch(
        setDetailListDataAction({
          deliveryList: resList.data.deliveryDetailVO,
          returnInfoList: resList.data.returnInfoVO,
          cancelInfoList: resList.data.cancelInfoVO,
        }),
      )
      dispatch(
        setCurrentRadioDictAction({
          商品明细: {
            key: 'productDetail',
            value: '商品明细',
            keyName: '商品明细',
          },
          发货明细: {
            key: 'deliveryDetail',
            value: `发货明细(${resList?.data.deliveryDetailVO.length || 0})`,
            keyName: '发货明细',
          },
          退货明细: {
            key: 'returnDetail',
            value: `退货明细(${resList?.data.returnInfoVO.length || 0})`,
            keyName: '退货明细',
          },
          取消明细: {
            key: 'cancelDetail',
            value: `取消明细(${resList?.data.cancelInfoVO.length || 0})`,
            keyName: '取消明细',
          },
        }),
      )
      // 接口不再返回imgUrl字段，此时默认设置为loading值，页面以此为标识渲染loading状态
      // 待获取图片接口调用通过后修改图片路径
      const productList = resList.data.productDetailVO.map((item) => ({
        ...item,
        imgUrl: 'loading',
        isDisabled: !!String(item.transferSupplierName ?? '').trim(),
      }))
      // 设置商品明细列表
      dispatch(setProductListAction(productList))
      // 调用接口获取商品图
      const resPicList = await BATCH_GET_PRODUCT_PICTURE({
        productCodes: productList.map((item) => item.code),
      })
      if (isResponseCodeSuccess(resPicList.code)) {
        const newProductList = productList.map((item) => {
          const itemUrl = resPicList.data.find(
            (term) => term.productCode === item.code,
          )?.pictureUrl
          // 修改图片路径，无路径的商品也以返回的空串覆盖之前设置的loading值
          return {
            ...item,
            imgUrl: itemUrl,
          }
        })
        dispatch(setProductListAction(newProductList))
      }
    } else {
      message.warning(res.msg)
    }
  }

  const handleDeliveryClick = async () => {
    if (selectedRows.length < 1) {
      message.error('请先选中货物')
      return
    }
    if (selectedRows.length > MAX_SUPPLIER_DELIVERY_COUNT) {
      message.warning(
        `您好，您只能勾选${MAX_SUPPLIER_DELIVERY_COUNT}行物料进行操作`,
      )
      return
    }
    const transferredForDelivery = selectedRows.filter((row) =>
      String(row?.transferSupplierName ?? '').trim(),
    )
    if (transferredForDelivery.length > 0) {
      const labels = transferredForDelivery
        .map((row) => {
          const name = String(row?.name ?? '').trim()
          const code = String(row?.code ?? '').trim()
          if (name) return name
          if (code) return `物料编码${code}`
          return ''
        })
        .filter(Boolean)
      message.error(
        labels.length > 0
          ? `${labels.join('、')}已被转派无法再进行发货！`
          : '所选物料已被转派无法再进行发货！',
      )
      return
    }
    const date = new Date()
    if (
      date.getHours() === 23 &&
      date.getMinutes() >= 30 &&
      date.getMinutes() <= 59
    ) {
      message.error('您好，23:30~24:00为系统每日维护时间，无法发货，请稍后再试')
      return
    }
    history.push({
      pathname: '/srm/adddeliveryorder',
      state: {
        ...detailInfo,
        selectedProductList: selectedRows,
      },
    })
  }

  /** 订单转派：勾选 / 已转派行 / 开票状态 / 退货取消数量 */
  const handleOrderTransferClick = () => {
    if (selectedRows.length === 0) {
      message.warning('请勾选物料行进行转派')
      return
    }

    /** 状态是否是“开票中、已开票”，如果是则抛错 */
    if (
      isSupplierInvoiceStatusBlockingTransfer(
        detailInfo?.accountOpenInvoiceStatus,
      )
    ) {
      message.error(ORDER_TRANSFER_INVOICE_BLOCK_MSG)
      return
    }

    // 可转派数量：物料行数量 - 取消数 - 退货数
    const invalidRows = getFullyReturnedOrCancelledRows(selectedRows)
    if (invalidRows.length > 0) {
      const codes = invalidRows
        .map((row) => String(row?.code ?? '').trim())
        .filter(Boolean)
      message.error(
        codes.length > 0
          ? `${codes
              .map((c) => `物料编码${c}`)
              .join('、')}物料全部退货或取消，无法转派！`
          : '所选物料行已全部退货或取消，无法转派！',
      )
      return
    }

    // 是否被转派
    const alreadyTransferredRows = selectedRows.filter((row) =>
      String(row?.transferSupplierName ?? '').trim(),
    )
    if (alreadyTransferredRows.length > 0) {
      const codes = alreadyTransferredRows
        .map((row) => String(row?.code ?? '').trim())
        .filter(Boolean)
      message.error(
        codes.length > 0
          ? `${codes
              .map((c) => `物料编码${c}`)
              .join('、')}已被转派，无法再进行转派！`
          : '所选物料行已被转派，无法再进行转派！',
      )
      return
    }

    if (!detailInfo?.id) {
      message.error('订单信息异常，无法转派')
      return
    }
    // 跳转订单转派页：query 供刷新/缓存 key；state 传本次勾选物料（见 ordertransfer/hooks 初始化优先级）
    history.push({
      pathname: `${SUPROUTE}/ordertransfer`,
      search: `?orderId=${encodeURIComponent(detailInfo.id)}`,
      state: {
        orderId: detailInfo.id,
        orderNo: detailInfo.orderNo ?? '',
        materialRows: selectedRows,
        type: detailInfo.platformCode || '',
      },
    })
  }

  const getInvoiceInfo = async (params: InvoiceInfoParam) => {
    const res = await GET_INVOICE_INFO_BY_ID(params)
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setInvoiceInfoAction(res.data))
    }
  }

  // 商品明细-根据进度筛选
  const onProgressChange = (value: any) => {
    dispatch(setProgressValueAction(value))
    const filterRes = value
      ? productList.filter((item: any) => item.progress === value)
      : productList
    dispatch(setProductListAction(filterRes))
  }

  const validateOrderAcceptUploadTime = () => {
    const allShipTime = detailInfo?.allShipTime
    if (
      !allShipTime ||
      Date.now() < allShipTime + ORDER_ACCEPT_UPLOAD_DELAY_MILLIS
    ) {
      message.warning('请在全部发货后三小时后再上传签收凭证')
      return false
    }
    return true
  }

  const showUploadCertificateModal = () => {
    if (!validateOrderAcceptUploadTime()) return
    dispatch(
      setUploadCertificateModalOptionAction({
        ...uploadCertificateModalOption,
        visible: true,
      }),
    )
  }

  const cancelUploadCertificateModal = () => {
    dispatch(
      setUploadCertificateModalOptionAction({
        ...uploadCertificateModalOption,
        visible: false,
      }),
    )
    dispatch(setCurrentEditCertificateAction(undefined))
  }

  const handleSubmitEnd = () => {
    cancelUploadCertificateModal()
  }

  const handleDelCertificate = async (record: AcceptInfoItem) => {
    CustomerConfirm({
      title: '是否确认删除该凭证',
      content: '确认删除？',
      async onOk() {
        // 删除本次上传的数据则不调用接口
        if (record.createTime === '-') {
          const list = detailInfo?.acceptInfoDTOS.filter(
            (item) => item.id !== record.id,
          )
          dispatch(
            setDetailInfoAction({
              ...detailInfo,
              acceptInfoDTOS: list,
            }),
          )
        } else {
          const res = await SUBMIT_ORDER_ACCEPT({
            dataInfo: [
              {
                fileIdList: [],
                id: record.id,
                type: record.type,
              },
            ],
            orderId: detailInfo?.id ?? '',
          })
          if (isResponseCodeSuccess(res.code)) {
            message.success('操作成功')
            refreshData()
          }
        }
      },
    })
  }

  const handleEditCertificate = (record: AcceptInfoItem) => {
    dispatch(setCurrentEditCertificateAction(record))
    showUploadCertificateModal()
  }

  const progressList = [
    {
      name: '待履约',
      description: '请核对订单并发货',
      time: detailInfo?.firstShipTime
        ? moment(detailInfo?.firstShipTime).format(timeFormatStr)
        : '',
    },
    {
      name: '履约中',
      description: '请供应商全部发货',
      time: detailInfo?.allShipTime
        ? moment(detailInfo?.allShipTime).format(timeFormatStr)
        : '',
    },
    {
      name: '待验收',
      description: '上传客户签收凭证',
      time: detailInfo?.confirmVoucherTime
        ? moment(detailInfo?.confirmVoucherTime).format(timeFormatStr)
        : '',
    },
    {
      name: '已完成',
      description: '咸亨确认签收凭证后，订单完成',
      time: detailInfo?.confirmVoucherTime
        ? moment(detailInfo?.confirmVoucherTime).format(timeFormatStr)
        : '',
    },
  ]

  const getCurrentProgress = () => {
    const currentState = detailInfo?.orderState ?? ''
    for (let i = progressList.length - 1; i >= 0; i--) {
      if (progressList[i].name === currentState) {
        return i
      }
    }
    return 0
  }

  const downloadAllCertificate = () => {
    DOWNLOAD_ACCEPT_TEMP({ id: detailInfo?.id ?? '' })
  }

  const downloadCustomCertificate = () => {
    DOWNLOAD_CUSTOM_ACCEPT_TEMP({ id: detailInfo?.id ?? '' })
  }

  const handleJumpInvoice = () => {
    switch (detailInfo?.invoicingState) {
      case '未开票':
        handleJumpAddInvoice([detailInfo.id])
        break
      case '开票中':
      case '已开票':
        history.replace({
          pathname: `${SUPROUTE}/invoiceApplicationDetail`,
          state: {
            orderId: detailInfo.orderInvoiceId,
            applicationNumber: detailInfo.invoiceApplicationNum,
          },
        })
    }
  }

  useEffect(() => {
    if (id) {
      getDetailInfo()
    }
    // 在订单详情页面时，点击消息中心跳转，订单id发生变化重新获取数据
    // eslint-disable-next-line
  }, [id])

  const cancelPreviewModal = () => {
    dispatch(setIsPreviewModalVisibleAction(false))
  }

  const showPreviewModal = (url: string, watermarkOption?: WaterMarkOption) => {
    dispatch(setPreviewFileUrlAction(url))
    dispatch(setWaterMarkOptionAction(watermarkOption))
    dispatch(setIsPreviewModalVisibleAction(true))
  }

  const showTemplateModal = (url: string) => {
    const newUrl = baseUrl + url
    dispatch(setPreviewFileUrlAction(newUrl))
    dispatch(setIsPreviewModalVisibleAction(true))
  }
  // 上传签收凭证
  const getAddOrderAccept = (val: any) => {
    const list: AcceptInfoItem[] =
      detailInfo?.acceptInfoDTOS.filter((item) => item.id !== val.id) || []
    const newItem = {
      acceptList: val.uploadFilesList,
      createTime: '-',
      id: val.id,
      type: val.type,
      uploadMan: GetLocalStorage('USER_NAME'),
    }
    dispatch(
      setDetailInfoAction({
        ...detailInfo,
        acceptInfoDTOS: [...list, newItem],
      }),
    )
  }

  // 保存并提交审核
  const saveAndSubmitCheck = async () => {
    if (!detailInfo?.acceptInfoDTOS.some((item) => item.acceptList.length)) {
      message.warning('请先上传附件再提交审核')
      return
    }
    if (!validateOrderAcceptUploadTime()) return
    if (detailInfo?.acceptInfoDTOS) {
      const data = detailInfo?.acceptInfoDTOS.map((item) => {
        return {
          fileIdList: item.acceptList.map((term) => term.id),
          id: item.id.length === 32 ? item.id : '',
          type: item.type,
        }
      })
      const res = await SUBMIT_ORDER_ACCEPT({
        dataInfo: data,
        orderId: detailInfo?.id,
      })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg)
        refreshData()
      }
    }
  }

  // 上传合同
  const inTableUploadfile = async (file: RcFile) => {
    // 上传当前合同的附件
    const res = await UPLOAD_ORDER_PURCHASE_CONTRACT(id, file)
    if (isResponseCodeSuccess(res.code)) {
      message.success('上传成功')
      getDetailInfo()
    }
  }

  // 下载文件
  function onClickDown(fileItem: any) {
    const newUrl = fileItem.url
    const url = `${baseUrl}${newUrl}`
    download(url)
  }

  // 导出合同
  const downLoadTemplate = async () => {
    await DOWNLOAD_PURCHASEORDERCONTRACT({ orderId: id })
  }

  // 删除文件
  async function onClickDel(data: any) {
    CustomerConfirm({
      title: '提示',
      content: '此操作将永久删除附件，是否继续?',
      onOk: async () => {
        const promise = DETELE_FILE({ id: data.id || '' })
        const res = await promise
        if (isResponseCodeSuccess(res.code)) {
          message.success('删除成功')
          getDetailInfo()
        } else {
          message.warning('删除失败')
        }
      },
    })
  }

  async function onClickDelDes(fileItem: any) {
    const filterList = orderDescriptionFiles.filter(
      (item) => item.url !== fileItem.url,
    )
    dispatch(setOrderDescriptionFilesAction(filterList))
  }

  const openModal = async (isOpen: boolean) => {
    if (isOpen) {
      getDes()
    } else {
      dispatch(setIsOpenModalAction(isOpen))
      if (richTextRef.current) {
        richTextRef.current.restate()
      }
    }
  }

  const getDes = async () => {
    const res = await GET_ORDER_DESCRIPTION(id)
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setOrderDescriptionFilesAction(res.data.orderDescriptionFiles))
      dispatch(setRichTextAction(res.data.orderDescriptionTextField))
      dispatch(setIsOpenModalAction(true))
    }
  }
  // 富文本
  const handleContentChange = (e: any) => {
    dispatch(setRichTextAction(e))
  }

  const uploadRichfile = async (file: RcFile) => {
    const res = await UPLOAD_ORDER_DESCRIPTION(id, file)
    if (isResponseCodeSuccess(res.code)) {
      const newFileList = [...orderDescriptionFiles, res.data[0]]
      dispatch(setOrderDescriptionFilesAction(newFileList))
      message.success('上传成功')
    } else {
      message.warning('上传失败')
    }
  }

  const submitRichText = async () => {
    if (richText === '<p></p>' && !orderDescriptionFiles.length) {
      message.warning('请上传文件附件或填写内容')
      return
    }
    const params = {
      orderId: id,
      orderDescriptionTextField: richText,
      orderDescriptionFileList: orderDescriptionFiles,
    }
    const res = await ADD_ORDER_DESCRIPTION(params)
    if (isResponseCodeSuccess(res.code)) {
      message.success('提交成功')
      dispatch(setIsOpenModalAction(false))
    }
  }

  return {
    baseUrl,
    hookstate: state,
    progressList,
    getCurrentProgress,
    handleCheckedChange,
    handleDeliveryClick,
    handleOrderTransferClick,
    handleTableChangeCheck,
    onProgressChange,
    showUploadCertificateModal,
    cancelUploadCertificateModal,
    handleDelCertificate,
    handleEditCertificate,
    handleSubmitEnd,
    downloadAllCertificate,
    downloadCustomCertificate,
    handleJumpInvoice,
    showPreviewModal,
    cancelPreviewModal,
    saveAndSubmitCheck,
    getAddOrderAccept,
    downLoadTemplate,
    inTableUploadfile,
    onClickDown,
    showTemplateModal,
    onClickDel,
    openModal,
    handleContentChange,
    submitRichText,
    uploadRichfile,
    onClickDelDes,
    richTextRef,
    refreshData,
  }
}
export default UserOrderDetailHook
import { message } from 'antd'
import { useEffect, useReducer, useRef } from 'react'
import { history } from '../../redux/store'
import {
  AcceptInfoItem,
  DOWNLOAD_ACCEPT_TEMP,
  GET_INVOICE_INFO_BY_ID,
  GET_ORDER_DETAIL,
  InvoiceInfoParam,
  SUBMIT_ORDER_ACCEPT,
  GET_ORDER_PRODUCT_INFO,
  BATCH_GET_PRODUCT_PICTURE,
  DOWNLOAD_PURCHASEORDERCONTRACT,
  UPLOAD_ORDER_PURCHASE_CONTRACT,
  DETELE_FILE,
  GET_ORDER_DESCRIPTION,
  ADD_ORDER_DESCRIPTION,
  UPLOAD_ORDER_DESCRIPTION,
  DOWNLOAD_CUSTOM_ACCEPT_TEMP,
} from '../../api/orderManage'
import {
  download,
  GetLocalStorage,
  isResponseCodeSuccess,
  timeFormatStr,
} from '../../tools/util'
import { useQuery } from '../../tools/utilHooks'
import moment from 'moment'
import { CustomerConfirm } from '../../components/ModalUser'
import { SUPROUTE } from '../../route'
import { handleJumpAddInvoice } from '../order-invoice/hooks'
import { WaterMarkOption } from '../../components/previewModal'
import reducer, {
  initialState,
  setCheckedRadioKeyAction,
  setSelectedRowsAction,
  setLoadingAction,
  setDetailInfoAction,
  setProductListAction,
  setCurrentRadioDictAction,
  setInvoiceInfoAction,
  setProgressValueAction,
  setUploadCertificateModalOptionAction,
  setCurrentEditCertificateAction,
  setIsPreviewModalVisibleAction,
  setPreviewFileUrlAction,
  setWaterMarkOptionAction,
  setIsTableLoadingAction,
  setDetailListDataAction,
  setIsOpenModalAction,
  setRichTextAction,
  setOrderDescriptionFilesAction,
} from './reducer'
import { MAX_SUPPLIER_DELIVERY_COUNT } from '../../global/config'
import { RcFile } from 'antd/lib/upload'
import {
  openInvoiceStateDict,
  openInvoiceStateDictInvert,
} from '../../tools/state'

const ORDER_TRANSFER_INVOICE_BLOCK_MSG =
  '当前订单开票状态为“开票、开票中”若需要转派请联系咸亨相关人员进行处理！'
const ORDER_ACCEPT_UPLOAD_DELAY_MILLIS = 3 * 60 * 60 * 1000

/** 仅「未开票」允许发起转派；其余（审核中/暂存/已开票/驳回等）拦截 */
function isSupplierInvoiceStatusBlockingTransfer(
  accountOpenInvoiceStatus: string | undefined,
): boolean {
  const raw = accountOpenInvoiceStatus
  const code = String(raw ?? '').trim()
  const unInvoiced = openInvoiceStateDict['未开票'].numstr
  if (code === unInvoiced || raw === '未开票') {
    return false
  }
  const nameFromCode = openInvoiceStateDictInvert?.[code]?.name
  if (nameFromCode === '未开票') {
    return false
  }
  return true
}

/** 可转派数量：物料行数量 - 取消数 - 退货数 */
function getProductTransferRemainQty(row: {
  num?: number
  cancelCount?: number
  reCount?: number
}): number {
  const n = Number(row?.num ?? 0)
  const cancel = Number(row?.cancelCount ?? 0)
  const ret = Number(row?.reCount ?? 0)
  if ([n, cancel, ret].some((v) => Number.isNaN(v))) {
    return 0
  }
  return n - cancel - ret
}

function getFullyReturnedOrCancelledRows(rows: any[]) {
  return rows.filter((row) => getProductTransferRemainQty(row) <= 0)
}

const UserOrderDetailHook = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const richTextRef = useRef<any>(null)
  const {
    detailInfo,
    selectedRows,
    productList,
    uploadCertificateModalOption,
    richText,
    orderDescriptionFiles,
  } = state
  const query = useQuery()
  const id = query.get('id') || ''
  const baseUrl = GetLocalStorage('BASEURL')
  /**
   * 表格多选选择框选中事件
   * @param key
   */
  const handleCheckedChange = (key: any) => {
    dispatch(setCheckedRadioKeyAction(key))
  }

  const handleTableChangeCheck = (
    selectedRowKeys: any,
    selectedRows: any[],
  ) => {
    dispatch(setSelectedRowsAction(selectedRows))
  }

  const refreshData = () => {
    getDetailInfo()
  }

  // 获取详情数据
  const getDetailInfo = async () => {
    dispatch(setLoadingAction(true))
    getInvoiceInfo({
      orderId: id,
    })
    const res = await GET_ORDER_DETAIL({
      orderId: id,
    })
    dispatch(setLoadingAction(false))
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setDetailInfoAction(res.data))
    } else {
      message.warning(res.msg)
    }
    dispatch(setIsTableLoadingAction(true))
    const resList = await GET_ORDER_PRODUCT_INFO({
      orderId: id,
    })
    dispatch(setIsTableLoadingAction(false))
    if (isResponseCodeSuccess(resList.code)) {
      dispatch(
        setDetailListDataAction({
          deliveryList: resList.data.deliveryDetailVO,
          returnInfoList: resList.data.returnInfoVO,
          cancelInfoList: resList.data.cancelInfoVO,
        }),
      )
      dispatch(
        setCurrentRadioDictAction({
          商品明细: {
            key: 'productDetail',
            value: '商品明细',
            keyName: '商品明细',
          },
          发货明细: {
            key: 'deliveryDetail',
            value: `发货明细(${resList?.data.deliveryDetailVO.length || 0})`,
            keyName: '发货明细',
          },
          退货明细: {
            key: 'returnDetail',
            value: `退货明细(${resList?.data.returnInfoVO.length || 0})`,
            keyName: '退货明细',
          },
          取消明细: {
            key: 'cancelDetail',
            value: `取消明细(${resList?.data.cancelInfoVO.length || 0})`,
            keyName: '取消明细',
          },
        }),
      )
      // 接口不再返回imgUrl字段，此时默认设置为loading值，页面以此为标识渲染loading状态
      // 待获取图片接口调用通过后修改图片路径
      const productList = resList.data.productDetailVO.map((item) => ({
        ...item,
        imgUrl: 'loading',
        isDisabled: !!String(item.transferSupplierName ?? '').trim(),
      }))
      // 设置商品明细列表
      dispatch(setProductListAction(productList))
      // 调用接口获取商品图
      const resPicList = await BATCH_GET_PRODUCT_PICTURE({
        productCodes: productList.map((item) => item.code),
      })
      if (isResponseCodeSuccess(resPicList.code)) {
        const newProductList = productList.map((item) => {
          const itemUrl = resPicList.data.find(
            (term) => term.productCode === item.code,
          )?.pictureUrl
          // 修改图片路径，无路径的商品也以返回的空串覆盖之前设置的loading值
          return {
            ...item,
            imgUrl: itemUrl,
          }
        })
        dispatch(setProductListAction(newProductList))
      }
    } else {
      message.warning(res.msg)
    }
  }

  const handleDeliveryClick = async () => {
    if (selectedRows.length < 1) {
      message.error('请先选中货物')
      return
    }
    if (selectedRows.length > MAX_SUPPLIER_DELIVERY_COUNT) {
      message.warning(
        `您好，您只能勾选${MAX_SUPPLIER_DELIVERY_COUNT}行物料进行操作`,
      )
      return
    }
    const transferredForDelivery = selectedRows.filter((row) =>
      String(row?.transferSupplierName ?? '').trim(),
    )
    if (transferredForDelivery.length > 0) {
      const labels = transferredForDelivery
        .map((row) => {
          const name = String(row?.name ?? '').trim()
          const code = String(row?.code ?? '').trim()
          if (name) return name
          if (code) return `物料编码${code}`
          return ''
        })
        .filter(Boolean)
      message.error(
        labels.length > 0
          ? `${labels.join('、')}已被转派无法再进行发货！`
          : '所选物料已被转派无法再进行发货！',
      )
      return
    }
    const date = new Date()
    if (
      date.getHours() === 23 &&
      date.getMinutes() >= 30 &&
      date.getMinutes() <= 59
    ) {
      message.error('您好，23:30~24:00为系统每日维护时间，无法发货，请稍后再试')
      return
    }
    history.push({
      pathname: '/srm/adddeliveryorder',
      state: {
        ...detailInfo,
        selectedProductList: selectedRows,
      },
    })
  }

  /** 订单转派：勾选 / 已转派行 / 开票状态 / 退货取消数量 */
  const handleOrderTransferClick = () => {
    if (selectedRows.length === 0) {
      message.warning('请勾选物料行进行转派')
      return
    }

    /** 状态是否是“开票中、已开票”，如果是则抛错 */
    if (
      isSupplierInvoiceStatusBlockingTransfer(
        detailInfo?.accountOpenInvoiceStatus,
      )
    ) {
      message.error(ORDER_TRANSFER_INVOICE_BLOCK_MSG)
      return
    }

    // 可转派数量：物料行数量 - 取消数 - 退货数
    const invalidRows = getFullyReturnedOrCancelledRows(selectedRows)
    if (invalidRows.length > 0) {
      const codes = invalidRows
        .map((row) => String(row?.code ?? '').trim())
        .filter(Boolean)
      message.error(
        codes.length > 0
          ? `${codes
              .map((c) => `物料编码${c}`)
              .join('、')}物料全部退货或取消，无法转派！`
          : '所选物料行已全部退货或取消，无法转派！',
      )
      return
    }

    // 是否被转派
    const alreadyTransferredRows = selectedRows.filter((row) =>
      String(row?.transferSupplierName ?? '').trim(),
    )
    if (alreadyTransferredRows.length > 0) {
      const codes = alreadyTransferredRows
        .map((row) => String(row?.code ?? '').trim())
        .filter(Boolean)
      message.error(
        codes.length > 0
          ? `${codes
              .map((c) => `物料编码${c}`)
              .join('、')}已被转派，无法再进行转派！`
          : '所选物料行已被转派，无法再进行转派！',
      )
      return
    }

    if (!detailInfo?.id) {
      message.error('订单信息异常，无法转派')
      return
    }
    // 跳转订单转派页：query 供刷新/缓存 key；state 传本次勾选物料（见 ordertransfer/hooks 初始化优先级）
    history.push({
      pathname: `${SUPROUTE}/ordertransfer`,
      search: `?orderId=${encodeURIComponent(detailInfo.id)}`,
      state: {
        orderId: detailInfo.id,
        orderNo: detailInfo.orderNo ?? '',
        materialRows: selectedRows,
        type: detailInfo.platformCode || '',
      },
    })
  }

  const getInvoiceInfo = async (params: InvoiceInfoParam) => {
    const res = await GET_INVOICE_INFO_BY_ID(params)
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setInvoiceInfoAction(res.data))
    }
  }

  // 商品明细-根据进度筛选
  const onProgressChange = (value: any) => {
    dispatch(setProgressValueAction(value))
    const filterRes = value
      ? productList.filter((item: any) => item.progress === value)
      : productList
    dispatch(setProductListAction(filterRes))
  }

  const validateOrderAcceptUploadTime = () => {
    const allShipTime = detailInfo?.allShipTime
    if (
      !allShipTime ||
      Date.now() < allShipTime + ORDER_ACCEPT_UPLOAD_DELAY_MILLIS
    ) {
      message.warning('请在全部发货后三小时后再上传签收凭证')
      return false
    }
    return true
  }

  const showUploadCertificateModal = () => {
    if (!validateOrderAcceptUploadTime()) return
    dispatch(
      setUploadCertificateModalOptionAction({
        ...uploadCertificateModalOption,
        visible: true,
      }),
    )
  }

  const cancelUploadCertificateModal = () => {
    dispatch(
      setUploadCertificateModalOptionAction({
        ...uploadCertificateModalOption,
        visible: false,
      }),
    )
    dispatch(setCurrentEditCertificateAction(undefined))
  }

  const handleSubmitEnd = () => {
    cancelUploadCertificateModal()
  }

  const handleDelCertificate = async (record: AcceptInfoItem) => {
    CustomerConfirm({
      title: '是否确认删除该凭证',
      content: '确认删除？',
      async onOk() {
        // 删除本次上传的数据则不调用接口
        if (record.createTime === '-') {
          const list = detailInfo?.acceptInfoDTOS.filter(
            (item) => item.id !== record.id,
          )
          dispatch(
            setDetailInfoAction({
              ...detailInfo,
              acceptInfoDTOS: list,
            }),
          )
        } else {
          const res = await SUBMIT_ORDER_ACCEPT({
            dataInfo: [
              {
                fileIdList: [],
                id: record.id,
                type: record.type,
              },
            ],
            orderId: detailInfo?.id ?? '',
          })
          if (isResponseCodeSuccess(res.code)) {
            message.success('操作成功')
            refreshData()
          }
        }
      },
    })
  }

  const handleEditCertificate = (record: AcceptInfoItem) => {
    dispatch(setCurrentEditCertificateAction(record))
    showUploadCertificateModal()
  }

  const progressList = [
    {
      name: '待履约',
      description: '请核对订单并发货',
      time: detailInfo?.firstShipTime
        ? moment(detailInfo?.firstShipTime).format(timeFormatStr)
        : '',
    },
    {
      name: '履约中',
      description: '请供应商全部发货',
      time: detailInfo?.allShipTime
        ? moment(detailInfo?.allShipTime).format(timeFormatStr)
        : '',
    },
    {
      name: '待验收',
      description: '上传客户签收凭证',
      time: detailInfo?.confirmVoucherTime
        ? moment(detailInfo?.confirmVoucherTime).format(timeFormatStr)
        : '',
    },
    {
      name: '已完成',
      description: '咸亨确认签收凭证后，订单完成',
      time: detailInfo?.confirmVoucherTime
        ? moment(detailInfo?.confirmVoucherTime).format(timeFormatStr)
        : '',
    },
  ]

  const getCurrentProgress = () => {
    const currentState = detailInfo?.orderState ?? ''
    for (let i = progressList.length - 1; i >= 0; i--) {
      if (progressList[i].name === currentState) {
        return i
      }
    }
    return 0
  }

  const downloadAllCertificate = () => {
    DOWNLOAD_ACCEPT_TEMP({ id: detailInfo?.id ?? '' })
  }

  const downloadCustomCertificate = () => {
    DOWNLOAD_CUSTOM_ACCEPT_TEMP({ id: detailInfo?.id ?? '' })
  }

  const handleJumpInvoice = () => {
    switch (detailInfo?.invoicingState) {
      case '未开票':
        handleJumpAddInvoice([detailInfo.id])
        break
      case '开票中':
      case '已开票':
        history.replace({
          pathname: `${SUPROUTE}/invoiceApplicationDetail`,
          state: {
            orderId: detailInfo.orderInvoiceId,
            applicationNumber: detailInfo.invoiceApplicationNum,
          },
        })
    }
  }

  useEffect(() => {
    if (id) {
      getDetailInfo()
    }
    // 在订单详情页面时，点击消息中心跳转，订单id发生变化重新获取数据
    // eslint-disable-next-line
  }, [id])

  const cancelPreviewModal = () => {
    dispatch(setIsPreviewModalVisibleAction(false))
  }

  const showPreviewModal = (url: string, watermarkOption?: WaterMarkOption) => {
    dispatch(setPreviewFileUrlAction(url))
    dispatch(setWaterMarkOptionAction(watermarkOption))
    dispatch(setIsPreviewModalVisibleAction(true))
  }

  const showTemplateModal = (url: string) => {
    const newUrl = baseUrl + url
    dispatch(setPreviewFileUrlAction(newUrl))
    dispatch(setIsPreviewModalVisibleAction(true))
  }
  // 上传签收凭证
  const getAddOrderAccept = (val: any) => {
    const list: AcceptInfoItem[] =
      detailInfo?.acceptInfoDTOS.filter((item) => item.id !== val.id) || []
    const newItem = {
      acceptList: val.uploadFilesList,
      createTime: '-',
      id: val.id,
      type: val.type,
      uploadMan: GetLocalStorage('USER_NAME'),
    }
    dispatch(
      setDetailInfoAction({
        ...detailInfo,
        acceptInfoDTOS: [...list, newItem],
      }),
    )
  }

  // 保存并提交审核
  const saveAndSubmitCheck = async () => {
    if (!detailInfo?.acceptInfoDTOS.some((item) => item.acceptList.length)) {
      message.warning('请先上传附件再提交审核')
      return
    }
    if (!validateOrderAcceptUploadTime()) return
    if (detailInfo?.acceptInfoDTOS) {
      const data = detailInfo?.acceptInfoDTOS.map((item) => {
        return {
          fileIdList: item.acceptList.map((term) => term.id),
          id: item.id.length === 32 ? item.id : '',
          type: item.type,
        }
      })
      const res = await SUBMIT_ORDER_ACCEPT({
        dataInfo: data,
        orderId: detailInfo?.id,
      })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg)
        refreshData()
      }
    }
  }

  // 上传合同
  const inTableUploadfile = async (file: RcFile) => {
    // 上传当前合同的附件
    const res = await UPLOAD_ORDER_PURCHASE_CONTRACT(id, file)
    if (isResponseCodeSuccess(res.code)) {
      message.success('上传成功')
      getDetailInfo()
    }
  }

  // 下载文件
  function onClickDown(fileItem: any) {
    const newUrl = fileItem.url
    const url = `${baseUrl}${newUrl}`
    download(url)
  }

  // 导出合同
  const downLoadTemplate = async () => {
    await DOWNLOAD_PURCHASEORDERCONTRACT({ orderId: id })
  }

  // 删除文件
  async function onClickDel(data: any) {
    CustomerConfirm({
      title: '提示',
      content: '此操作将永久删除附件，是否继续?',
      onOk: async () => {
        const promise = DETELE_FILE({ id: data.id || '' })
        const res = await promise
        if (isResponseCodeSuccess(res.code)) {
          message.success('删除成功')
          getDetailInfo()
        } else {
          message.warning('删除失败')
        }
      },
    })
  }

  async function onClickDelDes(fileItem: any) {
    const filterList = orderDescriptionFiles.filter(
      (item) => item.url !== fileItem.url,
    )
    dispatch(setOrderDescriptionFilesAction(filterList))
  }

  const openModal = async (isOpen: boolean) => {
    if (isOpen) {
      getDes()
    } else {
      dispatch(setIsOpenModalAction(isOpen))
      if (richTextRef.current) {
        richTextRef.current.restate()
      }
    }
  }

  const getDes = async () => {
    const res = await GET_ORDER_DESCRIPTION(id)
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setOrderDescriptionFilesAction(res.data.orderDescriptionFiles))
      dispatch(setRichTextAction(res.data.orderDescriptionTextField))
      dispatch(setIsOpenModalAction(true))
    }
  }
  // 富文本
  const handleContentChange = (e: any) => {
    dispatch(setRichTextAction(e))
  }

  const uploadRichfile = async (file: RcFile) => {
    const res = await UPLOAD_ORDER_DESCRIPTION(id, file)
    if (isResponseCodeSuccess(res.code)) {
      const newFileList = [...orderDescriptionFiles, res.data[0]]
      dispatch(setOrderDescriptionFilesAction(newFileList))
      message.success('上传成功')
    } else {
      message.warning('上传失败')
    }
  }

  const submitRichText = async () => {
    if (richText === '<p></p>' && !orderDescriptionFiles.length) {
      message.warning('请上传文件附件或填写内容')
      return
    }
    const params = {
      orderId: id,
      orderDescriptionTextField: richText,
      orderDescriptionFileList: orderDescriptionFiles,
    }
    const res = await ADD_ORDER_DESCRIPTION(params)
    if (isResponseCodeSuccess(res.code)) {
      message.success('提交成功')
      dispatch(setIsOpenModalAction(false))
    }
  }

  return {
    baseUrl,
    hookstate: state,
    progressList,
    getCurrentProgress,
    handleCheckedChange,
    handleDeliveryClick,
    handleOrderTransferClick,
    handleTableChangeCheck,
    onProgressChange,
    showUploadCertificateModal,
    cancelUploadCertificateModal,
    handleDelCertificate,
    handleEditCertificate,
    handleSubmitEnd,
    downloadAllCertificate,
    downloadCustomCertificate,
    handleJumpInvoice,
    showPreviewModal,
    cancelPreviewModal,
    saveAndSubmitCheck,
    getAddOrderAccept,
    downLoadTemplate,
    inTableUploadfile,
    onClickDown,
    showTemplateModal,
    onClickDel,
    openModal,
    handleContentChange,
    submitRichText,
    uploadRichfile,
    onClickDelDes,
    richTextRef,
    refreshData,
  }
}
export default UserOrderDetailHook
